import { Scalar, parse, stringify } from "yaml";

import {
  collectComponentSubtree,
  readSchemeNodePosition,
} from "@/interfaceBuilder/componentSchemeTree";
import { normalizeUiComponentId } from "@/utils/uiComponentLinks";
import type { StoredUiComponent } from "@/storage/uiComponentsRepository";
import type { StoredUiSidebarItem } from "@/storage/uiSidebarItemsRepository";

// RFC 9512 media type of the exported document.
export const YAML_MIME_TYPE = "application/yaml";

// How the exported document addresses the ecosystem's own resources. It mirrors
// exordos/manifests/ecosystem_ui_store.yaml.j2, the manifest that builds a
// constructor page from scratch.
const COMPONENTS_RESOURCE = "$exordos_ecosystem.ui.components";
const SIDEBAR_ITEMS_RESOURCE = "$exordos_ecosystem.ui.sidebar_items";
const PROJECT_IMPORT = "eco_project";
const MANIFEST_NAME_PREFIX = "ecosystem_ui_";
const MANIFEST_VERSION = "1.0.0";
const ANY_VERSION = "0.0.0";
const SIDEBAR_ITEM_KIND = "sidebar_item";

/** One component of an imported manifest, still addressed by its key. */
export interface ImportedManifestComponent {
  key: string;
  parentKey: string | null;
  props: Record<string, unknown>;
}

/** The sidebar item of an imported manifest and the page it opens. */
export interface ImportedSidebarItemManifest {
  // Parents above the children that link to them.
  components: readonly ImportedManifestComponent[];
  sidebarItem: {
    componentKey: string | null;
    description: string;
    name: string;
    // Everything the item carries beyond its own identity: icon, group,
    // permissions, public path and translations.
    props: Record<string, unknown>;
  };
}

/**
 * Writes a sidebar item and the whole component subtree it opens as an element
 * manifest, the format `exordos em elements install` reads.
 *
 * The tree is flat here, exactly as the manifest format wants it: every
 * component is a resource of its own and points at its parent with a
 * `parent_uuid` link, siblings keeping the `position` they were ordered by. A
 * `props` block is written whole -- the agent hashes the value it reads back,
 * so a block relying on model defaults would never match the target state.
 */
export function buildSidebarItemManifestYaml(
  item: StoredUiSidebarItem,
  components: readonly StoredUiComponent[],
): string {
  const manifestName = createManifestName(item);
  const projectId = `$${manifestName}.imports.$${PROJECT_IMPORT}:uuid`;
  const rootComponent = findLinkedComponent(item, components);
  // Depth-first from the page root, so a parent is always declared above the
  // children that link to it. Siblings are walked in the order they are laid
  // out, because the subtree keeps the order it is handed them in.
  const subtree = rootComponent
    ? collectComponentSubtree(sortComponents(components), rootComponent)
    : [];
  const keysByUuid = createComponentKeys(subtree);

  const manifest = {
    name: manifestName,
    description: createManifestDescription(item),
    schema_version: 1,
    version: quoted(MANIFEST_VERSION),
    api_version: "v1",
    requirements: {
      exordos_ecosystem: { from_version: quoted(ANY_VERSION) },
      core: { from_version: quoted(ANY_VERSION) },
    },
    // The page is filed under the ecosystem's own project -- the administration
    // project every account reads -- instead of an IAM project of its own, so
    // installing the manifest creates nothing but the page.
    imports: {
      [PROJECT_IMPORT]: {
        element: "$exordos_ecosystem",
        kind: "resource",
        link: "$core.iam.projects.$eco_project",
      },
    },
    resources: {
      ...(subtree.length
        ? {
          [COMPONENTS_RESOURCE]: Object.fromEntries(subtree.map((component) => [
            keysByUuid.get(component.uuid),
            {
              project_id: projectId,
              props: createComponentProps(component, keysByUuid),
            },
          ])),
        }
        : {}),
      [SIDEBAR_ITEMS_RESOURCE]: {
        [createSidebarItemKey(item)]: {
          project_id: projectId,
          props: createSidebarItemProps(
            item,
            rootComponent ? keysByUuid.get(rootComponent.uuid) : undefined,
          ),
        },
      },
    },
  };

  return `${createManifestHeader(item)}\n\n${stringify(manifest, { lineWidth: 0 })}`;
}

export function createSidebarItemManifestFileName(
  item: StoredUiSidebarItem,
): string {
  const slug = item.name
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");

  return `${slug || item.uuid}.yaml`;
}

// A version stays a string even when it is edited down to two numbers, which
// plain YAML would read as a float.
function quoted(value: string): Scalar {
  const scalar = new Scalar(value);
  scalar.type = Scalar.QUOTE_DOUBLE;
  return scalar;
}

function createManifestHeader(item: StoredUiSidebarItem): string {
  const name = item.name.replace(/\s+/g, " ").trim();

  return [
    "# Exported from the Exordos interface builder: the",
    `# "${name}" menu item and every component of the page it opens.`,
    "#",
    "# Install it with:",
    `#   exordos em elements install ${createSidebarItemManifestFileName(item)}`,
  ].join("\n");
}

function createManifestName(item: StoredUiSidebarItem): string {
  return `${MANIFEST_NAME_PREFIX}${createIdentifier(item.name) || "export"}`;
}

function createManifestDescription(item: StoredUiSidebarItem): string {
  return item.description.trim()
    || `${item.name} page exported from the interface builder`;
}

function createSidebarItemKey(item: StoredUiSidebarItem): string {
  return `${createIdentifier(item.name) || "sidebar"}_item`;
}

function findLinkedComponent(
  item: StoredUiSidebarItem,
  components: readonly StoredUiComponent[],
): StoredUiComponent | undefined {
  const componentId = normalizeUiComponentId(
    item.componentUuid || item.component?.uuid,
  );

  return componentId
    ? components.find((component) => component.uuid === componentId)
    : undefined;
}

/**
 * Names every component resource after the component itself, falling back to
 * its kind for a name that yields no identifier. Keys are kept apart from each
 * other because the manifest addresses a resource by its key alone.
 */
function createComponentKeys(
  subtree: readonly StoredUiComponent[],
): Map<string, string> {
  const keysByUuid = new Map<string, string>();
  const usedKeys = new Set<string>();

  subtree.forEach((component, index) => {
    const identifier = createIdentifier(component.name);
    const defaultKey = `${readKind(component)}_${index + 1}`;
    let key = identifier && !/^\d/.test(identifier) ? identifier : defaultKey;
    let suffix = 2;
    while (usedKeys.has(key)) {
      key = `${identifier || defaultKey}_${suffix}`;
      suffix += 1;
    }

    usedKeys.add(key);
    keysByUuid.set(component.uuid, key);
  });

  return keysByUuid;
}

function createComponentProps(
  component: StoredUiComponent,
  keysByUuid: ReadonlyMap<string, string>,
): Record<string, unknown> {
  const {
    children: _children,
    uuid: _uuid,
    ...props
  } = component.uiScheme as Record<string, unknown>;
  // A component whose parent is outside the exported subtree becomes a page
  // root of its own, the way the sidebar item opens it.
  const parentKey = component.parentUuid
    ? keysByUuid.get(component.parentUuid)
    : undefined;

  return {
    ...props,
    kind: readKind(component),
    parent_uuid: parentKey ? `${COMPONENTS_RESOURCE}.$${parentKey}:uuid` : null,
    // The text of the component in the languages it was translated into: a page
    // installed elsewhere is read in the languages it was built for.
    translations: readTranslations(props),
  };
}

function createSidebarItemProps(
  item: StoredUiSidebarItem,
  rootComponentKey: string | undefined,
): Record<string, unknown> {
  const props = isRecord(item.props) ? item.props : {};

  return {
    ...props,
    kind: SIDEBAR_ITEM_KIND,
    name: item.name,
    description: item.description,
    // The heading the item is drawn under: an item installed elsewhere keeps
    // the group it was built in.
    group: typeof props.group === "string" ? props.group : "",
    position: item.position,
    component_uuid: rootComponentKey
      ? `${COMPONENTS_RESOURCE}.$${rootComponentKey}:uuid`
      : null,
    icon: props.icon ?? null,
    // The permissions the item is shown under travel with it: an exported
    // manifest states every field of its kind, and an item installed elsewhere
    // has to keep the audience it was built for.
    permissions: Array.isArray(props.permissions) ? props.permissions : [],
    // The path the page answers at for a visitor who is not signed in. It
    // travels with the item for the same reason its permissions do: an
    // exported manifest states every field of its kind.
    public_path: typeof props.public_path === "string" ? props.public_path : "",
    translations: readTranslations(props),
  };
}

function readTranslations(props: Record<string, unknown>): unknown[] {
  return Array.isArray(props.translations) ? props.translations : [];
}

// Mirrors how the builder orders siblings, so the manifest reads in the order
// the tree and the rendered page show.
function sortComponents(
  components: readonly StoredUiComponent[],
): StoredUiComponent[] {
  return [...components].sort((a, b) => (
    readSchemeNodePosition(a.uiScheme) - readSchemeNodePosition(b.uiScheme)
    || a.name.localeCompare(b.name)
    || a.uuid.localeCompare(b.uuid)
  ));
}

function readKind(component: StoredUiComponent): string {
  const kind = (component.uiScheme as Record<string, unknown>).kind;

  return typeof kind === "string" ? kind : component.componentType;
}

// Manifest keys address resources, so they carry nothing but lowercase latin
// letters, digits and underscores.
function createIdentifier(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Reads back a manifest the builder exported: the sidebar item and the flat
 * component tree it opens, addressed by the manifest keys instead of uuids.
 *
 * The keys are what the components link each other by here, because an
 * exported manifest carries no uuids at all -- the installation it is read into
 * names its own components. A reference that names nothing in the file leaves
 * the component at the top of the tree, the way the sidebar item opens it.
 *
 * Throws when the document is not a manifest of this kind.
 */
export function parseSidebarItemManifestYaml(
  text: string,
): ImportedSidebarItemManifest {
  const manifest = parse(text) as unknown;
  const resources = isRecord(manifest) && isRecord(manifest.resources)
    ? manifest.resources
    : null;
  if (!resources) {
    throw new Error("The document declares no resources.");
  }

  const sidebarItem = readSidebarItemResource(resources[SIDEBAR_ITEMS_RESOURCE]);
  if (!sidebarItem) {
    throw new Error("The document declares no sidebar item.");
  }

  return {
    components: sortComponentsByParent(
      readComponentResources(resources[COMPONENTS_RESOURCE]),
    ),
    sidebarItem,
  };
}

function readComponentResources(value: unknown): ImportedManifestComponent[] {
  if (!isRecord(value)) return [];

  const entries = Object.entries(value).flatMap(([key, resource]) => {
    const props = isRecord(resource) && isRecord(resource.props)
      ? resource.props
      : null;
    if (!props) return [];

    const {
      children: _children,
      parent_uuid: parentReference,
      uuid: _uuid,
      ...componentProps
    } = props;

    return [{
      key,
      parentKey: readComponentKeyReference(parentReference),
      props: componentProps,
    }];
  });
  const keys = new Set(entries.map((entry) => entry.key));

  return entries.map((entry) => (
    entry.parentKey && keys.has(entry.parentKey)
      ? entry
      : { ...entry, parentKey: null }
  ));
}

function readSidebarItemResource(
  value: unknown,
): ImportedSidebarItemManifest["sidebarItem"] | null {
  if (!isRecord(value)) return null;

  for (const resource of Object.values(value)) {
    const props = isRecord(resource) && isRecord(resource.props)
      ? resource.props
      : null;
    const name = props && typeof props.name === "string" ? props.name.trim() : "";
    if (!props || !name) continue;

    // Everything the create request states itself is dropped here, so the
    // props carry nothing but what travels with the item: its icon, group,
    // permissions, public path and translations.
    const {
      component_uuid: componentReference,
      description,
      kind: _kind,
      name: _name,
      position: _position,
      uuid: _uuid,
      ...itemProps
    } = props;

    return {
      componentKey: readComponentKeyReference(componentReference),
      description: typeof description === "string" ? description : "",
      name,
      props: itemProps,
    };
  }

  return null;
}

// A manifest addresses a component by the key it is declared under, never by a
// uuid: `$exordos_ecosystem.ui.components.$nodes_page:uuid`.
function readComponentKeyReference(value: unknown): string | null {
  if (typeof value !== "string") return null;

  const prefix = `${COMPONENTS_RESOURCE}.$`;
  const suffix = ":uuid";
  if (!value.startsWith(prefix) || !value.endsWith(suffix)) return null;

  return value.slice(prefix.length, -suffix.length) || null;
}

/**
 * Parents above the children that link to them, so the tree can be built in one
 * pass. Components the file leaves in a cycle keep the order they are declared
 * in; the import writes them as they are rather than dropping them.
 */
function sortComponentsByParent(
  components: readonly ImportedManifestComponent[],
): ImportedManifestComponent[] {
  const remaining = [...components];
  const ordered: ImportedManifestComponent[] = [];
  const orderedKeys = new Set<string>();

  let progressed = true;
  while (remaining.length && progressed) {
    progressed = false;
    for (let index = 0; index < remaining.length;) {
      const component = remaining[index];
      if (component.parentKey && !orderedKeys.has(component.parentKey)) {
        index += 1;
        continue;
      }

      ordered.push(component);
      orderedKeys.add(component.key);
      remaining.splice(index, 1);
      progressed = true;
    }
  }

  return [...ordered, ...remaining];
}
